import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { getPushKey, savePush, deletePush } from "./clubApi";
import { isIOS, isStandalone, pushSupported } from "./device";
import { Btn, Card } from "./ui";

const toBytes = (v) => {
  const p = v + "=".repeat((4 - (v.length % 4)) % 4);
  return Uint8Array.from(atob(p.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
};

export default function PushCard({ token, incFirst }) {
  const [state, setState] = useState("loading"); // loading | off | on | denied | need-install | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return setState(isIOS() && !isStandalone() ? "need-install" : "unsupported");
    if (isIOS() && !isStandalone()) return setState("need-install");
    if (Notification.permission === "denied") return setState("denied");
    const t = setTimeout(() => setState((s) => (s === "loading" ? "off" : s)), 4000);
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => setState(s ? "on" : "off")).catch(() => setState("off"));
    const onVis = () => { if (document.visibilityState === "visible" && Notification.permission !== "denied") setState((s) => (s === "denied" ? "off" : s)); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "denied" : "off");
        if (perm === "denied") toast.message("Il telefono tiene ancora le notifiche bloccate: segui i passaggi qui sotto");
        setBusy(false); return;
      }
      const { key } = await getPushKey();
      const reg = await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(key) }));
      await savePush(token, sub.toJSON());
      setState("on"); toast.success("Promemoria attivi: ti scriviamo noi al momento giusto");
    } catch { toast.error("Non riesco ad attivare i promemoria su questo telefono"); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await deletePush(token, sub.endpoint); await sub.unsubscribe(); }
      setState("off"); toast.message("Promemoria disattivati");
    } catch { toast.error("Riprova tra un attimo"); }
    setBusy(false);
  };

  if (state === "loading" || state === "unsupported") return null;

  if (state === "need-install") return (
    <Card className="flex gap-3 items-start" data-testid="push-need-install">
      <Bell size={20} className="text-[#9A948A] mt-0.5 shrink-0" />
      <div className="text-sm"><p className="font-semibold">Vuoi che ti ricordiamo noi il check e la routine?</p><p className="text-[#9A948A] mt-1">Su iPhone i promemoria funzionano solo dopo aver salvato la pagina sulla schermata Home (vedi sotto). Fatto quello, apri l'icona Diamond Club e torna qui.</p></div>
    </Card>
  );

  if (state === "denied") return (
    <Card className="space-y-3" data-testid="push-denied">
      <div className="flex gap-3 items-start">
        <BellOff size={20} className="text-[#9A948A] mt-0.5 shrink-0" />
        <div className="text-sm"><p className="font-semibold">Promemoria non attivi</p><p className="text-[#9A948A] mt-1">Hai detto «Non consentire». Prova a riattivarli da qui: se il telefono non te lo richiede, segui i due passaggi sotto.</p></div>
      </div>
      <Btn className="w-full" onClick={enable} disabled={busy} data-testid="push-retry-btn"><Bell size={16} /> {busy ? "Riprovo…" : "Riprova ad attivare"}</Btn>
      <div className="rounded-2xl bg-[#0E0E10] p-3 text-xs text-[#9A948A] leading-relaxed" data-testid="push-denied-steps">
        {isIOS()
          ? <>Su iPhone: apri <b className="text-[#F4EFE6]">Impostazioni</b> → <b className="text-[#F4EFE6]">Notifiche</b> → scorri fino a <b className="text-[#F4EFE6]">Diamond Club</b> → attiva <b className="text-[#F4EFE6]">Consenti notifiche</b>. Poi torna qui e premi «Riprova».</>
          : <>Su Android: tieni premuta l'icona <b className="text-[#F4EFE6]">Diamond Club</b> → <b className="text-[#F4EFE6]">Info app</b> → <b className="text-[#F4EFE6]">Notifiche</b> → attiva. Oppure in Chrome: tocca il <b className="text-[#F4EFE6]">lucchetto</b> accanto all'indirizzo → <b className="text-[#F4EFE6]">Autorizzazioni</b> → Notifiche → Consenti. Poi premi «Riprova».</>}
      </div>
    </Card>
  );

  if (state === "on") return (
    <Card className="flex items-center justify-between gap-3" data-testid="push-on">
      <div className="flex gap-3 items-center"><BellRing size={20} className="text-[#E5B13A] shrink-0" /><div className="text-sm"><p className="font-semibold">Promemoria attivi</p><p className="text-xs text-[#9A948A]">Routine, check e riordino: te li ricordiamo noi.</p></div></div>
      <button onClick={disable} disabled={busy} className="text-xs text-[#6F6A62] hover:text-white whitespace-nowrap" data-testid="push-disable-btn">Disattiva</button>
    </Card>
  );

  return (
    <Card className="border-[#E5B13A]/30 space-y-3" data-testid="push-off">
      <div className="flex gap-3 items-start"><Bell size={20} className="text-[#E5B13A] mt-0.5 shrink-0" /><div className="text-sm"><p className="font-semibold">Ti ricordiamo noi, con gentilezza</p><p className="text-[#9A948A] mt-1">Una notifica al mattino per la routine, una quando è ora del check e una quando un prodotto sta per finire. Niente pubblicità, solo quello. Il telefono ti chiederà il permesso: tocca «Consenti».</p></div></div>
      <Btn className="w-full" onClick={enable} disabled={busy} data-testid="push-enable-btn"><Bell size={16} /> {busy ? "Attivo…" : "Attiva i promemoria"}</Btn>
    </Card>
  );
}
